import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerApi } from '../services/customerApi';
import { useNotification } from '../components/Notification';
import { generateMobileDeLink } from '../utils/mobileDeLink';
import Spinner from '../components/Spinner';
import './Cars.css';
import './DetailPages.css';

function CustomerCarDetail() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [expandedReminders, setExpandedReminders] = useState(true);
  const [expandedDefects, setExpandedDefects] = useState(true);
  const [expandedAccidents, setExpandedAccidents] = useState(true);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [showDefectForm, setShowDefectForm] = useState(false);
  const [showAccidentForm, setShowAccidentForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [editingDefect, setEditingDefect] = useState(null);
  const [editingAccident, setEditingAccident] = useState(null);
  const [reminderFormData, setReminderFormData] = useState({ title: '', date: '', mileage: '', notes: '', completed: false });
  const [defectFormData, setDefectFormData] = useState({ title: '', description: '', date: '', mileage: '' });
  const [accidentFormData, setAccidentFormData] = useState({ title: '', description: '', date: '', mileage: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    if (!token) {
      navigate('/customer-login');
      return;
    }
    if (id) {
      loadCar();
    }
  }, [id]);

  const loadCar = async () => {
    try {
      setLoading(true);
      const data = await customerApi.get(`/customer-cars/${id}`);
      setCar(data);
    } catch (error) {
      console.error('Error loading car:', error);
      showNotification('Fehler beim Laden des Autos', 'error');
      navigate('/customer-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const handleSaveReminder = async () => {
    if (!reminderFormData.title.trim() || !reminderFormData.date) return;

    try {
      if (editingReminder) {
        await customerApi.put(`/customer-cars/${id}/reminders/${editingReminder.id}`, {
          title: reminderFormData.title,
          date: reminderFormData.date,
          mileage: reminderFormData.mileage ? parseInt(reminderFormData.mileage) : null,
          notes: reminderFormData.notes,
          completed: reminderFormData.completed
        });
      } else {
        await customerApi.post(`/customer-cars/${id}/reminders`, {
          title: reminderFormData.title,
          date: reminderFormData.date,
          mileage: reminderFormData.mileage ? parseInt(reminderFormData.mileage) : null,
          notes: reminderFormData.notes,
          completed: false
        });
      }
      await loadCar();
      setShowReminderForm(false);
      setEditingReminder(null);
      setReminderFormData({ title: '', date: '', mileage: '', notes: '', completed: false });
    } catch (error) {
      console.error('Error saving reminder:', error);
      showNotification('Fehler beim Speichern der Erinnerung', 'error');
    }
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setReminderFormData({
      title: reminder.title || '',
      date: reminder.date ? reminder.date.split('T')[0] : '',
      mileage: reminder.mileage ? String(reminder.mileage) : '',
      notes: reminder.notes || '',
      completed: reminder.completed || false
    });
    setShowReminderForm(true);
  };

  const handleToggleReminderComplete = async (reminder) => {
    try {
      await customerApi.put(`/customer-cars/${id}/reminders/${reminder.id}`, {
        title: reminder.title,
        date: reminder.date,
        mileage: reminder.mileage,
        notes: reminder.notes,
        completed: !reminder.completed
      });
      await loadCar();
    } catch (error) {
      console.error('Error toggling reminder:', error);
      showNotification('Fehler beim Aktualisieren der Erinnerung', 'error');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Erinnerung wirklich löschen?')) return;

    try {
      await customerApi.delete(`/customer-cars/${id}/reminders/${reminderId}`);
      await loadCar();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      showNotification('Fehler beim Löschen der Erinnerung', 'error');
    }
  };

  const handleSaveDefect = async () => {
    if (!defectFormData.title) {
      showNotification('Bitte geben Sie einen Titel ein', 'error');
      return;
    }

    try {
      if (editingDefect) {
        await customerApi.put(`/customer-cars/${id}/known-defects/${editingDefect.id}`, defectFormData);
      } else {
        await customerApi.post(`/customer-cars/${id}/known-defects`, defectFormData);
      }
      await loadCar();
      setShowDefectForm(false);
      setEditingDefect(null);
      setDefectFormData({ title: '', description: '', date: '', mileage: '' });
    } catch (error) {
      console.error('Error saving defect:', error);
      showNotification('Fehler beim Speichern des Defekts', 'error');
    }
  };

  const handleEditDefect = (defect) => {
    setEditingDefect(defect);
    setDefectFormData({
      title: defect.title || '',
      description: defect.description || '',
      date: defect.date ? defect.date.split('T')[0] : '',
      mileage: defect.mileage ? String(defect.mileage) : ''
    });
    setShowDefectForm(true);
  };

  const handleDeleteDefect = async (defectId) => {
    if (!window.confirm('Bekannten Defekt wirklich löschen?')) return;

    try {
      await customerApi.delete(`/customer-cars/${id}/known-defects/${defectId}`);
      await loadCar();
    } catch (error) {
      console.error('Error deleting defect:', error);
      showNotification('Fehler beim Löschen des Defekts', 'error');
    }
  };

  const handleSaveAccident = async () => {
    if (!accidentFormData.title) {
      showNotification('Bitte geben Sie einen Titel ein', 'error');
      return;
    }

    try {
      if (editingAccident) {
        await customerApi.put(`/customer-cars/${id}/accidents/${editingAccident.id}`, accidentFormData);
      } else {
        await customerApi.post(`/customer-cars/${id}/accidents`, accidentFormData);
      }
      await loadCar();
      setShowAccidentForm(false);
      setEditingAccident(null);
      setAccidentFormData({ title: '', description: '', date: '', mileage: '' });
    } catch (error) {
      console.error('Error saving accident:', error);
      showNotification('Fehler beim Speichern des Unfalls', 'error');
    }
  };

  const handleEditAccident = (accident) => {
    setEditingAccident(accident);
    setAccidentFormData({
      title: accident.title || '',
      description: accident.description || '',
      date: accident.date ? accident.date.split('T')[0] : '',
      mileage: accident.mileage ? String(accident.mileage) : ''
    });
    setShowAccidentForm(true);
  };

  const handleDeleteAccident = async (accidentId) => {
    if (!window.confirm('Unfall wirklich löschen?')) return;

    try {
      await customerApi.delete(`/customer-cars/${id}/accidents/${accidentId}`);
      await loadCar();
    } catch (error) {
      console.error('Error deleting accident:', error);
      showNotification('Fehler beim Löschen des Unfalls', 'error');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!car) {
    return <div>Auto nicht gefunden</div>;
  }

  return (
    <div className="customer-portal" style={{ padding: '2rem' }}>
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/customer-dashboard')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>{car.make} {car.model} {car.year}</h1>
        <button type="button" onClick={() => navigate(`/customer-cars/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>

      <div className="car-details-grid">
        <div className="detail-section">
          <h2>Fahrzeugdaten</h2>
          {car.image && (
            <div style={{ marginBottom: '1rem' }}>
              <img 
                src={car.image} 
                alt={`${car.make} ${car.model}`}
                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', marginBottom: '0.5rem' }}
              />
            </div>
          )}
          <p><strong>Kennzeichen:</strong> {car.licensePlate || '-'}</p>
          <p><strong>VIN:</strong> {car.vin || '-'}</p>
          {car.hsn && <p><strong>HSN:</strong> {car.hsn}</p>}
          {car.tsn && <p><strong>TSN:</strong> {car.tsn}</p>}
          {car.engineCode && <p><strong>Motorkennbuchstabe:</strong> {car.engineCode}</p>}
          {car.fuelType && <p><strong>Kraftstoffart:</strong> {car.fuelType === 'BENZIN' ? 'Benzin' : car.fuelType === 'DIESEL' ? 'Diesel' : car.fuelType === 'ELEKTRO' ? 'Elektro' : car.fuelType === 'HYBRID' ? 'Hybrid' : car.fuelType === 'LPG' ? 'LPG' : car.fuelType === 'CNG' ? 'CNG/Erdgas' : car.fuelType}</p>}
          {car.transmission && <p><strong>Getriebeart:</strong> {car.transmission === 'MANUAL' ? 'Manuell' : car.transmission === 'AUTOMATIC' ? 'Automatik' : car.transmission}</p>}
          <p><strong>Kilometerstand:</strong> {car.mileage?.toLocaleString('de-DE') || 0} km</p>
          {car.color && <p><strong>Farbe:</strong> {car.color}</p>}
          
          {(() => {
            const mobileDeUrl = generateMobileDeLink(car);
            return mobileDeUrl ? (
              <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Vergleichbare Fahrzeuge</h3>
                <a 
                  href={mobileDeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-small"
                  style={{ display: 'inline-block', textDecoration: 'none' }}
                >
                  Auf mobile.de suchen →
                </a>
              </div>
            ) : null;
          })()}
        </div>

        <div className="detail-section">
          <div 
            className="car-journal-header"
            onClick={() => setExpandedReminders(!expandedReminders)}
            style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
          >
            <span>Wartungserinnerungen ({(car.maintenanceReminders || []).length})</span>
            <span className="journal-toggle-arrow">{expandedReminders ? '▼' : '▶'}</span>
          </div>
          {expandedReminders && (
            <div>
              {(car.maintenanceReminders || []).length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Keine Erinnerungen vorhanden</p>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  {(car.maintenanceReminders || []).map(reminder => {
                    const reminderDate = new Date(reminder.date);
                    const now = new Date();
                    const daysUntil = Math.ceil((reminderDate - now) / (1000 * 60 * 60 * 24));
                    const isOverdue = reminderDate < now && !reminder.completed;
                    const isUpcoming = daysUntil <= 7 && daysUntil >= 0 && !reminder.completed;
                    
                    return (
                      <div 
                        key={reminder.id} 
                        style={{ 
                          marginBottom: '0.75rem', 
                          padding: '0.75rem', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px',
                          backgroundColor: reminder.completed ? '#f5f5f5' : (isOverdue ? '#ffebee' : (isUpcoming ? '#fff8e1' : '#fff')),
                          borderLeft: reminder.completed ? '4px solid #4caf50' : (isOverdue ? '4px solid #d32f2f' : (isUpcoming ? '4px solid #ff9800' : '4px solid #2196f3')),
                          opacity: reminder.completed ? 0.7 : 1
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <strong style={{ textDecoration: reminder.completed ? 'line-through' : 'none' }}>
                                {reminder.title || 'Wartungserinnerung'}
                              </strong>
                              {reminder.completed && <span style={{ color: '#4caf50', fontSize: '0.85rem' }}>✓ Erledigt</span>}
                              {isOverdue && !reminder.completed && <span style={{ color: '#d32f2f', fontSize: '0.85rem', fontWeight: 'bold' }}>⚠️ Überfällig</span>}
                              {isUpcoming && !reminder.completed && <span style={{ color: '#f57c00', fontSize: '0.85rem', fontWeight: 'bold' }}>⚠️ Anstehend</span>}
                            </div>
                            <div style={{ fontSize: '0.9em', color: '#666' }}>
                              <span>📅 {formatDate(reminder.date)}</span>
                              {reminder.mileage && <span style={{ marginLeft: '1rem' }}>📏 {reminder.mileage.toLocaleString('de-DE')} km</span>}
                            </div>
                            {reminder.notes && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                                {reminder.notes}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              type="button"
                              onClick={() => handleEditReminder(reminder)}
                              className="btn-small"
                              style={{ fontSize: '0.8rem' }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleReminderComplete(reminder)}
                              className="btn-small"
                              style={{ fontSize: '0.8rem', backgroundColor: reminder.completed ? '#ff9800' : '#4caf50', color: 'white' }}
                            >
                              {reminder.completed ? 'Rückgängig' : 'Erledigt'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="btn-small btn-danger"
                              style={{ fontSize: '0.8rem' }}
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {showReminderForm ? (
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                    {editingReminder ? 'Erinnerung bearbeiten' : 'Neue Erinnerung hinzufügen'}
                  </h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Titel *</label>
                    <input
                      type="text"
                      value={reminderFormData.title}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, title: e.target.value })}
                      placeholder="z.B. Ölwechsel, Inspektion, TÜV"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Datum *</label>
                    <input
                      type="date"
                      value={reminderFormData.date}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, date: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Kilometerstand (optional)</label>
                    <input
                      type="number"
                      value={reminderFormData.mileage}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, mileage: e.target.value })}
                      placeholder="z.B. 50000"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Notizen (optional)</label>
                    <textarea
                      value={reminderFormData.notes}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, notes: e.target.value })}
                      placeholder="Zusätzliche Informationen..."
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSaveReminder}
                      className="btn-small"
                      disabled={!reminderFormData.title || !reminderFormData.date}
                    >
                      {editingReminder ? 'Speichern' : 'Hinzufügen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowReminderForm(false);
                        setEditingReminder(null);
                        setReminderFormData({ title: '', date: '', mileage: '', notes: '', completed: false });
                      }}
                      className="btn-small btn-secondary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowReminderForm(true)}
                  className="btn-small"
                >
                  Erinnerung hinzufügen
                </button>
              )}
            </div>
          )}
        </div>

        <div className="detail-section">
          <div 
            className="car-journal-header"
            onClick={() => setExpandedDefects(!expandedDefects)}
            style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
          >
            <span>Bekannte Defekte ({(car.knownDefects || []).length})</span>
            <span className="journal-toggle-arrow">{expandedDefects ? '▼' : '▶'}</span>
          </div>
          {expandedDefects && (
            <div>
              {showDefectForm ? (
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9', marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                    {editingDefect ? 'Defekt bearbeiten' : 'Neuen Defekt hinzufügen'}
                  </h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Titel *</label>
                    <input
                      type="text"
                      value={defectFormData.title}
                      onChange={(e) => setDefectFormData({ ...defectFormData, title: e.target.value })}
                      placeholder="z.B. Bremsen quietschen"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Beschreibung</label>
                    <textarea
                      value={defectFormData.description}
                      onChange={(e) => setDefectFormData({ ...defectFormData, description: e.target.value })}
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Datum</label>
                    <input
                      type="date"
                      value={defectFormData.date}
                      onChange={(e) => setDefectFormData({ ...defectFormData, date: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Kilometerstand</label>
                    <input
                      type="number"
                      value={defectFormData.mileage}
                      onChange={(e) => setDefectFormData({ ...defectFormData, mileage: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSaveDefect}
                      className="btn-small"
                      disabled={!defectFormData.title}
                    >
                      {editingDefect ? 'Speichern' : 'Hinzufügen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDefectForm(false);
                        setEditingDefect(null);
                        setDefectFormData({ title: '', description: '', date: '', mileage: '' });
                      }}
                      className="btn-small btn-secondary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {(car.knownDefects || []).length === 0 ? (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>Keine Defekte vorhanden</p>
                  ) : (
                    <div style={{ marginBottom: '1rem' }}>
                      {(car.knownDefects || []).map(defect => (
                        <div key={defect.id} style={{ marginBottom: '0.75rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <strong>{defect.title}</strong>
                              {defect.description && <div style={{ marginTop: '0.25rem', fontSize: '0.9em', color: '#666' }}>{defect.description}</div>}
                              <div style={{ marginTop: '0.5rem', fontSize: '0.85em', color: '#666' }}>
                                {defect.date && <span>📅 {formatDate(defect.date)}</span>}
                                {defect.mileage && <span style={{ marginLeft: '1rem' }}>📏 {defect.mileage.toLocaleString('de-DE')} km</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <button
                                type="button"
                                onClick={() => handleEditDefect(defect)}
                                className="btn-small"
                                style={{ fontSize: '0.8rem' }}
                              >
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteDefect(defect.id)}
                                className="btn-small btn-danger"
                                style={{ fontSize: '0.8rem' }}
                              >
                                Löschen
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setShowDefectForm(true)}
                    className="btn-small"
                  >
                    Defekt hinzufügen
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="detail-section">
          <div 
            className="car-journal-header"
            onClick={() => setExpandedAccidents(!expandedAccidents)}
            style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
          >
            <span>Unfälle ({(car.accidents || []).length})</span>
            <span className="journal-toggle-arrow">{expandedAccidents ? '▼' : '▶'}</span>
          </div>
          {expandedAccidents && (
            <div>
              {showAccidentForm ? (
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9', marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                    {editingAccident ? 'Unfall bearbeiten' : 'Neuen Unfall hinzufügen'}
                  </h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Titel *</label>
                    <input
                      type="text"
                      value={accidentFormData.title}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, title: e.target.value })}
                      placeholder="z.B. Auffahrunfall"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Beschreibung</label>
                    <textarea
                      value={accidentFormData.description}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, description: e.target.value })}
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Datum</label>
                    <input
                      type="date"
                      value={accidentFormData.date}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, date: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Kilometerstand</label>
                    <input
                      type="number"
                      value={accidentFormData.mileage}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, mileage: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSaveAccident}
                      className="btn-small"
                      disabled={!accidentFormData.title}
                    >
                      {editingAccident ? 'Speichern' : 'Hinzufügen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAccidentForm(false);
                        setEditingAccident(null);
                        setAccidentFormData({ title: '', description: '', date: '', mileage: '' });
                      }}
                      className="btn-small btn-secondary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {(car.accidents || []).length === 0 ? (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>Keine Unfälle vorhanden</p>
                  ) : (
                    <div style={{ marginBottom: '1rem' }}>
                      {(car.accidents || []).map(accident => (
                        <div key={accident.id} style={{ marginBottom: '0.75rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <strong>{accident.title}</strong>
                              {accident.description && <div style={{ marginTop: '0.25rem', fontSize: '0.9em', color: '#666' }}>{accident.description}</div>}
                              <div style={{ marginTop: '0.5rem', fontSize: '0.85em', color: '#666' }}>
                                {accident.date && <span>📅 {formatDate(accident.date)}</span>}
                                {accident.mileage && <span style={{ marginLeft: '1rem' }}>📏 {accident.mileage.toLocaleString('de-DE')} km</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <button
                                type="button"
                                onClick={() => handleEditAccident(accident)}
                                className="btn-small"
                                style={{ fontSize: '0.8rem' }}
                              >
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAccident(accident.id)}
                                className="btn-small btn-danger"
                                style={{ fontSize: '0.8rem' }}
                              >
                                Löschen
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setShowAccidentForm(true)}
                    className="btn-small"
                  >
                    Unfall hinzufügen
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerCarDetail;
